<template>
	<Layout>
		<div class="rank-page">
			<div class="page-content" v-loading.fullscreen.lock="pageLoading" element-loading-text="转链中"
				element-loading-spinner="el-icon-loading" element-loading-background="rgba(0, 0, 0, 0.45)">
				<!-- 页面头部 -->
				<div class="page-header">
					<h2>{{ currentPlatform.name }}带货榜单</h2>
					<p>实时更新的热门商品排行榜，助您把握市场趋势</p>
				</div>

				<!-- 平台切换 -->
				<div class="tab-section">
					<el-tabs v-model="activeTab" @tab-click="handlePlatformChange" class="custom-tabs">
						<el-tab-pane v-for="(platform, index) in platformList" :key="platform.type"
							:label="platform.name" :name="index.toString()"></el-tab-pane>
					</el-tabs>
				</div>

				<!-- 筛选区域 -->
				<el-card class="card-container" shadow="never">
					<div class="filter-section">
						<!-- 榜单类型 -->
						<div class="filter-group">
							<label class="filter-label">榜单类型：</label>
							<el-radio-group v-model="rankType" @change="handleRankChange">
								<el-radio-button v-for="rank in rankList" :key="rank.type" :label="rank.type">{{
									rank.name }}</el-radio-button>
							</el-radio-group>
						</div>

						<!-- 时间筛选 -->
						<div class="filter-group">
							<label class="filter-label">时间范围：</label>
							<el-radio-group v-model="timeRange" @change="handleTimeChange">
								<el-radio-button v-for="time in timeList" :key="time.type" :label="time.type">{{
									time.name }}</el-radio-button>
							</el-radio-group>
						</div>
					</div>
				</el-card>

				<!-- 商品列表 -->
				<div class="goods-list" v-loading="loading">
					<div v-if="goodsList.length === 0" class="empty-data">
						{{ loading ? '数据加载中...' : '暂无榜单数据，请尝试切换其他条件' }}
					</div>

					<div class="goods-grid" v-else>
						<div class="goods-item" v-for="(item, index) in goodsList" :key="item.id || item.origin_id">
							<div class="goods-card">
								<!-- 排名标记 -->
								<div class="rank-badge" :class="getRankClass(index)">
									{{ index + 1 }}
								</div>

								<!-- 商品图片 -->
								<div class="goods-image">
									<el-image :src="item.image || defaultImage"
										:preview-src-list="[item.image || defaultImage]" fit="contain" lazy>
										<div slot="error" class="image-slot">
											<i class="el-icon-picture-outline"></i>
										</div>
									</el-image>
								</div>

								<!-- 商品信息 -->
								<div class="goods-content">
									<!-- 商品标题 -->
									<div class="goods-title" :title="item.title" @click="handleGoodsClick(item)">
										<el-tag size="mini" class="platform-tag"
											:style="{ backgroundColor: getPlatformColor(item.type), color: '#fff', borderColor: getPlatformColor(item.type) }">{{
												getPlatformName(item.type) }}</el-tag>
										{{ item.title }}
									</div>

									<!-- 统计数据 -->
									<div class="stats-info" v-if="item.sort_name && item.type != 25">
										<span class="stats-text">
											<template v-if="rankType == 2">
												已成交￥<span class="number customFont">{{ formatNumber(item.sort_name * 5)
												}}</span>+
											</template>
											<template v-else-if="rankType == 1">
												已出单<span class="number customFont">{{ Math.floor(item.sort_name * 5)
												}}</span>+单
											</template>
											<template v-else-if="rankType == 3">
												已赚佣金￥<span class="number customFont">{{ formatNumber(item.sort_name * 5)
												}}</span>+
											</template>
										</span>
									</div>

									<!-- 价格区域 -->
									<div class="price-row">
										<div class="price-info">
											<div class="current-price">
												￥<span class="price-number customFont">{{ item.final_price ||
													item.quan_price }}</span>
											</div>
											<div v-if="item.origin_price && item.origin_price != item.final_price"
												class="original-price">
												￥{{ item.origin_price }}
											</div>
										</div>

										<!-- 优惠标签 -->
										<div class="goods-tags">
											<el-tag v-if="item.coupon_price" type="danger" effect="dark"
												class="price-tag">券￥{{ item.coupon_price
												}}</el-tag>
											<el-tag v-if="item.fan_price" type="success" class="price-tag">分享赚￥{{
												item.fan_price }}</el-tag>
										</div>
									</div>

									<!-- 商品ID和操作 -->
									<div class="action-row">
										<div class="goods-id" @click="copyGoodsId(item.origin_id)">
											<el-tooltip content="点击复制商品ID" placement="top">
												<span class="id-text">
													<i class="el-icon-document-copy"></i> 复制商品ID
												</span>
											</el-tooltip>
										</div>

										<el-button type="primary" size="mini"
											@click="handleGoodsClick(item)">立即推广</el-button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- 加载更多 -->
				<div class="load-more" v-if="goodsList.length">
					<p v-if="loading" class="loading-text">
						<i class="el-icon-loading"></i> 加载中...
					</p>
					<p v-else-if="noMoreData" class="no-more-text">
						- 已经到底了 -
					</p>
					<p v-else class="load-more-text">
						下拉加载更多
					</p>
				</div>
			</div>
		</div>

		<!-- 商品详情处理器 -->
		<GoodsDetailHandler ref="goodsHandler" @loading-change="handleLoadingChange" @success="handleGoodsSuccess"
			@error="handleGoodsError" />
	</Layout>
</template>

<script>
import Layout from '@/components/Layout/index'
import GoodsDetailHandler from '@/components/GoodsDetailHandler'
import { getGrankList } from '@/api/public'

const PLATFORM_TYPE = {
	1: '淘宝',
	2: '淘宝',
	3: '京东',
	4: '拼多多',
	9: '唯品会',
	16: '美团',
	22: '快手',
	25: '抖音'
}

export default {
	components: {
		Layout,
		GoodsDetailHandler
	},
	data() {
		return {
			// 平台列表
			platformList: [
				{
					name: '淘宝',
					type: 1,
					color: '#ff6600'
				},
				{
					name: '京东',
					type: 3,
					color: '#e32e20'
				},
				{
					name: '抖音',
					type: 25,
					color: '#000000'
				},
				{
					name: '唯品会',
					type: 9,
					color: '#e91e63'
				},
				{
					name: '拼多多',
					type: 4,
					color: '#e32e20'
				},
				{
					name: '美团',
					type: 16,
					color: '#ffc300'
				},
				{
					name: '快手',
					type: 22,
					color: '#ff6437'
				}
			],
			activeTab: '0',
			platformType: 1,

			// 榜单类型
			rankList: [
				{
					name: '商品成交榜',
					type: 2
				},
				{
					name: '商品出单榜',
					type: 1
				},
				{
					name: '商品佣金榜',
					type: 3
				}
			],
			rankType: 2,

			// 时间筛选
			timeList: [
				{
					name: '今天',
					type: 1
				},
				{
					name: '昨天',
					type: 2
				},
				{
					name: '近7天',
					type: 3
				},
				{
					name: '近30天',
					type: 4
				}
			],
			timeRange: 1,

			// 数据
			goodsList: [],
			loading: false,

			// 分页
			pagination: {
				page: 1,
				pagesize: 20
			},
			noMoreData: false,
			scrollDistance: 100,  // 距离底部多少像素时触发加载

			// 商品详情


			// 默认图片
			defaultImage: 'https://img.huajuanyun.com/utools/2025/711/1752201507730.png',

			// 全局加载状态
			pageLoading: false
		}
	},
	computed: {
		currentPlatform() {
			return this.platformList[parseInt(this.activeTab)] || this.platformList[0]
		}
	},
	created() {
		// 初始化
	},
	mounted() {
		this.init()
		// 添加滚动监听
		window.addEventListener('scroll', this.handleScroll)
	},

	beforeDestroy() {
		// 移除滚动监听
		window.removeEventListener('scroll', this.handleScroll)
	},
	methods: {
		// 初始化
		init() {
			this.getGoodsList()
		},

		// 获取商品列表
		async getGoodsList(loadMore = false, append = false) {
			if (!loadMore) {
				this.pagination.page = 1
				this.noMoreData = false
			}

			this.loading = true

			try {
				const params = {
					type_big: this.platformType,
					times: this.timeRange,
					rank: this.rankType,
					page: this.pagination.page,
					pagesize: this.pagination.pagesize
				}

				const response = await getGrankList(params)

				if (response && response.data && response.data.list) {
					const list = response.data.list

					// 处理数据
					if (append) {
						this.goodsList = [...this.goodsList, ...list]
					} else {
						this.goodsList = list
					}

					// 如果返回的数据长度小于每页大小，说明没有更多数据了
					this.noMoreData = list.length < this.pagination.pagesize
				} else {
					// 接口异常或无数据，停止加载
					this.noMoreData = true
					if (!append) {
						this.goodsList = []
					}
				}
			} catch (error) {
				console.error('获取榜单数据失败:', error)
				this.noMoreData = true
				if (!append) {
					this.goodsList = []
				}
				this.$message.error('数据加载失败')
			} finally {
				this.loading = false
			}
		},



		// 平台切换
		handlePlatformChange(tab) {
			this.activeTab = tab.name
			this.platformType = this.platformList[parseInt(tab.name)].type
			// 重置数据并获取新数据
			this.pagination.page = 1
			this.noMoreData = false
			this.getGoodsList()
			// 回到顶部
			this.scrollToTop()
		},

		// 榜单类型切换
		handleRankChange(value) {
			this.rankType = value
			// 重置数据并获取新数据
			this.pagination.page = 1
			this.noMoreData = false
			this.getGoodsList()
			// 回到顶部
			this.scrollToTop()
		},

		// 时间切换
		handleTimeChange(value) {
			this.timeRange = value
			// 重置数据并获取新数据
			this.pagination.page = 1
			this.noMoreData = false
			this.getGoodsList()
			// 回到顶部
			this.scrollToTop()
		},

		// 回到顶部
		scrollToTop() {
			window.scrollTo({
				top: 0,
				behavior: 'smooth'
			})
		},

		// 处理滚动事件
		handleScroll() {
			// 如果正在加载或已经没有更多数据，则不处理
			if (this.loading || this.noMoreData) return

			const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
			const clientHeight = document.documentElement.clientHeight
			const scrollHeight = document.documentElement.scrollHeight

			// 当滚动到距离底部一定距离时，加载更多数据
			if (scrollHeight - scrollTop - clientHeight <= this.scrollDistance) {
				this.loadMore()
			}
		},

		// 加载更多数据
		loadMore() {
			// 页码加1
			this.pagination.page++
			// 加载更多数据，传入true表示追加模式
			this.getGoodsList(true, true)
		},

		// 处理商品点击
		handleGoodsClick(item) {
			console.log('榜单商品数据:', item)
			this.$refs.goodsHandler.handleGoodsClick(item)
		},

		// 处理加载状态变化
		handleLoadingChange(loading) {
			this.pageLoading = loading
			console.log('商品详情加载状态:', loading)
		},

		// 处理商品详情成功
		handleGoodsSuccess(data) {
			console.log('商品详情处理成功:', data)
		},

		// 处理商品详情错误
		handleGoodsError(error) {
			console.log('商品详情处理错误:', error)
		},

		// 获取排名样式
		getRankClass(index) {
			if (index === 0) return 'rank-first'
			if (index === 1) return 'rank-second'
			if (index === 2) return 'rank-third'
			return 'rank-normal'
		},

		// 获取平台名称
		getPlatformName(type) {
			return PLATFORM_TYPE[type] || '其他'
		},

		// 获取平台颜色
		getPlatformColor(type) {
			// 从platformList中查找对应平台的颜色
			const platform = this.platformList.find(p => p.type === type);
			if (platform) {
				return platform.color;
			}

			// 如果在platformList中找不到，使用默认颜色映射
			const colorMap = {
				1: '#ff6600', // 淘宝
				2: '#ff6600', // 天猫
				3: '#e32e20', // 京东
				4: '#f85f39', // 拼多多
				9: '#e91e63', // 唯品会
				16: '#ffc300', // 美团
				22: '#ff6437', // 快手
				25: '#000000'  // 抖音
			};

			return colorMap[type] || '#909399'; // 默认颜色
		},

		// 格式化数字
		formatNumber(num) {
			if (!num) return '0'
			const number = Math.floor(num)
			return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
		},

		// 提取真实商品ID
		extractRealGoodsId(originId) {
			if (!originId) return '-';

			try {
				// URL解码
				const decodedId = decodeURIComponent(originId);
				// 分割字符串
				const parts = decodedId.split('|');
				// 如果格式为 "type|goodsId|..." 则返回第二部分
				if (parts.length >= 2) {
					return parts[1];
				}
				// 如果不符合预期格式，返回原始ID
				return originId;
			} catch (error) {
				console.error('解析商品ID出错:', error);
				return originId;
			}
		},

		// 复制商品ID
		copyGoodsId(id) {
			if (!id) return;

			const realId = this.extractRealGoodsId(id);

			this.$copyText(realId).then(() => {
				this.$message.success('商品ID复制成功');
			}, () => {
				this.$message.error('复制失败，请手动复制');
			});
		}
	},

	filters: {
		filterSales(val) {
			let num = Number(val || 0)
			if (isNaN(num)) return val || 0
			return num > 9999 ? (Math.floor(num / 10000) + '万') : num
		}
	},
	watch: {
	}
}
</script>

<style lang="scss" scoped>
.rank-page {
	padding: 20px;
	min-height: calc(100vh - 60px);
	background-color: #f8f9fa;
}

.page-header {
	margin: 24px 0;

	h2 {
		margin: 0 0 8px 0;
		font-size: 24px;
		font-weight: 600;
		color: #303133;
	}

	p {
		margin: 0;
		color: #909399;
		font-size: 14px;
	}
}

.card-container {
	margin-bottom: 20px;
	border-radius: 8px;
	border: 1px solid #ebeef5;

	::v-deep .el-card__body {
		padding: 20px;
	}
}

.tab-section {
	margin-bottom: 20px;
	background: white;
	border-radius: 8px;
	border: 1px solid #ebeef5;
	padding: 0 20px;
}

.custom-tabs {
	::v-deep .el-tabs__header {
		margin: 0;
		border-bottom: 1px solid #e4e7ed;
	}

	::v-deep .el-tabs__item {
		font-size: 18px;
		font-weight: 600;
		height: 60px;
		line-height: 60px;
		color: #606266;
		padding: 0 30px;

		&.is-active {
			color: var(--main-bg-color);
			font-weight: 700;
		}

		&:hover {
			color: var(--main-bg-color);
		}
	}

	::v-deep .el-tabs__active-bar {
		display: none;
	}

	::v-deep .el-tabs__item {
		position: relative;

		&.is-active {
			position: relative;

			&::after {
				content: '';
				position: absolute;
				bottom: 0;
				left: 50%;
				transform: translateX(-50%);
				width: 30px;
				height: 4px;
				background-color: var(--main-bg-color);
				border-radius: 2px;
				z-index: 2;
			}
		}

		&:nth-child(2) {
			padding-left: 30px !important;
		}

		&:last-child {
			padding-right: 30px !important;
		}
	}

	::v-deep .el-tabs__nav-wrap {
		&::after {
			height: 1px;
		}
	}
}

.filter-section {
	.filter-group {
		display: flex;
		align-items: center;
		margin-bottom: 15px;

		&:last-child {
			margin-bottom: 0;
		}

		.filter-label {
			font-weight: 500;
			margin-right: 15px;
			min-width: 80px;
			color: #606266;
		}

		::v-deep {
			.el-radio-button__inner {
				border-radius: 6px;
				margin-right: 4px;
				border: 1px solid #dcdfe6;
			}

			.el-radio-button__orig-radio:checked+.el-radio-button__inner {
				box-shadow: none;
			}
		}


	}
}

.goods-list {
	.empty-data {
		padding: 40px 0;
		text-align: center;
		color: #909399;
		font-size: 14px;
	}

	.goods-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
		gap: 15px;
		width: 100%;
		margin: 0 auto;
	}

	.goods-item {
		margin-bottom: 15px;
	}

	.goods-card {
		position: relative;
		background-color: #fff;
		border-radius: 8px;
		box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
		transition: all 0.3s ease;
		height: 100%;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		min-height: 360px;

		&:hover {
			transform: translateY(-5px);
			box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
		}
	}

	.rank-badge {
		position: absolute;
		top: 5px;
		left: 5px;
		z-index: 10;
		width: 24px;
		height: 24px;
		line-height: 24px;
		text-align: center;
		border-radius: 50%;
		font-weight: bold;
		font-size: 12px;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

		&.rank-first {
			background: linear-gradient(45deg, #FFD700, #FFA500);
			color: white;
		}

		&.rank-second {
			background: linear-gradient(45deg, #C0C0C0, #A9A9A9);
			color: white;
		}

		&.rank-third {
			background: linear-gradient(45deg, #CD7F32, #B8860B);
			color: white;
		}

		&.rank-normal {
			background: #f0f0f0;
			color: #666;
		}
	}

	.goods-image {
		width: 100%;
		height: 0;
		padding-bottom: 100%;
		/* 1:1 比例 */
		position: relative;
		overflow: hidden;
		background-color: #f9f9f9;
		border-bottom: 1px solid #f0f0f0;

		::v-deep .el-image {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			display: flex;
			justify-content: center;
			align-items: center;

			img {
				max-width: 100%;
				max-height: 100%;
				object-fit: contain;
				border-radius: 8px 8px 0 0;
				transition: transform 0.3s ease;

				&:hover {
					transform: scale(1.05);
				}
			}
		}

		.image-slot {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 100%;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			background: #f5f7fa;
			color: #909399;
			font-size: 24px;
		}
	}

	.goods-content {
		padding: 10px;
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	.goods-title {
		font-size: 16px;
		color: #222222;
		line-height: 1.4;
		margin-bottom: 15px;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		min-height: 36px;
		font-weight: bold;
		cursor: pointer;

		.platform-tag {
			font-weight: normal;
			padding: 0 4px;
			border-radius: 2px;
			margin-right: 3px;
			vertical-align: middle;
			font-size: 13px;
		}
	}

	.stats-info {
		margin-bottom: 8px;

		.stats-text {
			font-size: 16px;
			color: #b5691a;
			font-weight: bold;
			background: #fff4e8;
			border: 1px solid rgba(181, 105, 26, .02);
			padding: 6px 0 8px;
			border-radius: 3px;
			display: block;
			text-align: center;

			.number {
				font-size: 20px;
				font-weight: bold;
			}
		}
	}

	.price-row {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: 8px;
	}

	.price-info {
		.current-price {
			color: #e2231a;
			font-weight: bold;

			.price-number {
				font-size: 21px;
			}
		}

		.original-price {
			color: #999;
			font-size: 13px;
			text-decoration: line-through;
			margin-top: 2px;
		}
	}

	.goods-tags {
		text-align: right;

		.price-tag {
			margin-left: 3px;
			margin-bottom: 3px;
			font-weight: bold;
			transform-origin: right center;
		}
	}

	.action-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-top: auto;
		padding-top: 8px;
		border-top: 1px solid #f0f0f0;

		.el-button {
			padding: 6px 10px;
			font-size: 12px;
		}
	}

	.goods-id {
		.id-text {
			font-size: 12px;
			color: var(--main-bg-color);
			cursor: pointer;
			display: inline-block;

			&:hover {
				text-decoration: underline;
			}

			i {
				margin-right: 3px;
			}
		}
	}
}

.load-more {
	text-align: center;
	padding: 20px 0;

	p {
		margin: 0;
		font-size: 14px;
		color: #909399;
	}

	.loading-text {
		color: var(--main-bg-color);

		i {
			margin-right: 5px;
		}
	}

	.no-more-text {
		color: #909399;
	}

	.load-more-text {
		color: #909399;
	}
}

// 响应式设计
@media (max-width: 1400px) {
	.goods-list {
		.goods-grid {
			grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
		}
	}
}

@media (max-width: 1200px) {
	.goods-list {
		.goods-grid {
			grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
			max-width: 1000px;
		}

		.goods-card {
			min-height: 340px;
		}
	}
}

@media (max-width: 992px) {
	.goods-list {
		.goods-grid {
			grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
			max-width: 800px;
		}

		.goods-card {
			min-height: 330px;
		}

		.goods-title {
			min-height: 38px;
			-webkit-line-clamp: 2;
			font-size: 13px;
		}
	}
}

@media (max-width: 768px) {
	.rank-page {
		padding: 10px;
	}

	.filter-section {
		.filter-group {
			flex-direction: column;
			align-items: flex-start;

			.filter-label {
				margin-bottom: 10px;
			}
		}
	}

	.goods-list {
		.goods-grid {
			grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
			gap: 10px;
		}

		.goods-card {
			min-height: 320px;
		}

		.goods-content {
			padding: 8px;
		}

		.goods-title {
			font-size: 12px;
			margin-bottom: 6px;
			min-height: 34px;
		}

		.price-info {
			.current-price .price-number {
				font-size: 15px;
			}
		}

		.stats-info .stats-text {
			font-size: 12px;
			padding: 2px 5px;
		}

		.action-row {
			flex-direction: column;
			align-items: flex-start;

			.goods-id {
				margin-bottom: 6px;
			}

			.el-button {
				width: 100%;
				padding: 5px 10px;
				font-size: 12px;
			}
		}
	}
}

@media (max-width: 576px) {
	.goods-list {
		.goods-grid {
			grid-template-columns: repeat(2, 1fr);
			gap: 8px;
		}

		.goods-card {
			min-height: 300px;
		}

		.goods-title {
			font-size: 12px;
		}
	}
}
</style>
